import React, { useMemo } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Alert, Box, CircularProgress } from '@mui/material';
import { DATA_GRID_DEFAULT_PAGE_SIZE } from './DataGridNumberedPagination';
import { DataGrid, GridActionsCellItem, type GridColDef } from '@mui/x-data-grid';
import type { StrapiUserTypeRow } from '../api/strapiUserTypes';

export type UserTypesGridCallbacks = {
  onEdit: (row: StrapiUserTypeRow) => void;
  onDelete: (row: StrapiUserTypeRow) => void;
};

type UserTypesDataGridProps = {
  rows: StrapiUserTypeRow[];
  isLoading: boolean;
  isError: boolean;
  error?: Error | null;
  callbacks: UserTypesGridCallbacks;
};

export function UserTypesDataGrid({
  rows,
  isLoading,
  isError,
  error,
  callbacks,
}: UserTypesDataGridProps) {
  const columns: GridColDef<StrapiUserTypeRow>[] = useMemo(
    () => [
      { field: 'type_code', headerName: 'Code', minWidth: 160, flex: 0.5 },
      { field: 'type_name', headerName: 'Name', minWidth: 200, flex: 0.6 },
      {
        field: 'description',
        headerName: 'Description',
        minWidth: 220,
        flex: 1,
        valueFormatter: (v: unknown) => (typeof v === 'string' && v.trim() ? v : '—'),
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: 100,
        getActions: (params) => {
          const row = params.row;
          return [
            <GridActionsCellItem
              key="edit"
              icon={<EditOutlinedIcon fontSize="small" />}
              label="Edit"
              onClick={() => callbacks.onEdit(row)}
            />,
            <GridActionsCellItem
              key="delete"
              icon={<DeleteOutlineIcon fontSize="small" />}
              label="Delete"
              onClick={() => callbacks.onDelete(row)}
            />,
          ];
        },
      },
    ],
    [callbacks],
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={32} />
      </Box>
    );
  }
  if (isError) {
    return <Alert severity="error">{error instanceof Error ? error.message : 'Unable to load user types.'}</Alert>;
  }

  return (
    <Box sx={{ width: '100%' }} data-testid="user-types-data-grid">
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(r) => r.documentId ?? `id:${r.numericId ?? ''}`}
        disableRowSelectionOnClick
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: DATA_GRID_DEFAULT_PAGE_SIZE, page: 0 } } }}
        autoHeight
        density="compact"
        sx={{ '& .MuiDataGrid-columnHeaders': { fontWeight: 600 } }}
      />
    </Box>
  );
}
